import { useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"

import useLoginCookie from "./useLoginCookie"
import useRoleCookie from "./useRoleCookie"
import useAlertModalAtom from "./useAlertModalAtom"

const useFetch = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [data, setData] = useState()

    const [cookie, , deleteCookie] = useLoginCookie()
    const [, , deleteRoleCookie] = useRoleCookie()
    const [, setAlert] = useAlertModalAtom()

    // Helper function to get current URL for callback
    const getCurrentUrl = () => {
        // Ensure pathname starts with / for safety
        const pathname = location.pathname.startsWith('/') ? location.pathname : '/' + location.pathname
        const currentPath = pathname + location.search
        
        // Define valid routes that can be used as callbacks
        const validRoutes = [
            '/',                    // Login page
            '/select',             // Event selection
        ]
        
        // Check if current path matches a valid route
        const isValidRoute = validRoutes.some(route => {
            if (route === '/') {
                return currentPath === '/' || currentPath.startsWith('/?')
            }
            return currentPath === route || currentPath.startsWith(route + '?')
        })
        
        // Check if it's a valid event timer route pattern
        const eventTimerPattern = /^\/event\/[^/]+\/timer(\?.*)?$/
        const isValidEventTimer = eventTimerPattern.test(currentPath)
        
        // If the current path is valid, return it encoded
        if (isValidRoute || isValidEventTimer) {
            return encodeURIComponent(currentPath)
        }
        
        // If invalid, return empty string to fall back to default behavior
        return null
    }

    const request = async (method, path, body) => {

        const convertResponse = {}

        try {
            const object = {
                "method": method,
                "headers": {}
            }

            // headers 삽입
            if (cookie) object["headers"]["token"] = cookie

            // body 삽입
            if (body) {
                object["headers"]["Content-Type"] = "application/json"
                object["body"] = JSON.stringify(body)
            }

            // 요청 전송
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, object)

            // 응답 내용 저장
            convertResponse.statusCode = response.status
            convertResponse.data = await response.json()

            // 공통 status code 후처리
            switch (response.status) {
                case 401:
                    if (convertResponse.data.error_code === "TOKEN_EXPIRED") {
                        setAlert("로그인 토큰이 만료되었습니다.\n다시 로그인해주세요.")
                        deleteCookie()
                        deleteRoleCookie()
                        const callbackUrl = getCurrentUrl()
                        navigate(callbackUrl ? `/?callback=${callbackUrl}` : '/')
                    }
                    else if (convertResponse.data.error_code === "TOKEN_INVALID") {
                        setAlert("허용되지 않은 로그인 토큰 입니다.\n다시 로그인해주세요.")
                        deleteCookie()
                        deleteRoleCookie()
                        const callbackUrl = getCurrentUrl()
                        navigate(callbackUrl ? `/?callback=${callbackUrl}` : '/')
                    }
                    else if (convertResponse.data.error_code === "TOKEN_MISSING") {
                        setAlert("로그인 토큰을 찾을 수 없습니다.")
                        deleteCookie()
                        deleteRoleCookie()
                        const callbackUrl = getCurrentUrl()
                        navigate(callbackUrl ? `/?callback=${callbackUrl}` : '/')
                    }
                    break
                case 403:
                    if (convertResponse.data.error_code === "UNAUTHORIZED") {
                        setAlert("접근 권한이 없습니다.")
                        const callbackUrl = getCurrentUrl()
                        navigate(callbackUrl ? `/?callback=${callbackUrl}` : '/')
                    }
                    break
                case 404:
                    if (convertResponse.data.error_code === "ROUTER_NOT_FOUND") setAlert("해당 백엔드 요청은 존재하지 않습니다.")
                    break
                case 500:
                    if (convertResponse.data.error_code === "INTERNAL_SERVER_ERROR") setAlert("서버에서 알 수 없는 오류가 발생하였습니다.\n관리자에게 문의해주세요.")
                    else navigate("/500")
                    break
                default:
                    // No specific handling for other status codes
                    break
            }

            // 데이터 후처리
            setData(convertResponse)

            if (process.env.REACT_APP_MODE === "develop") {
                console.group(`${method} ${path}`)
                console.log(body)
                console.log(convertResponse?.statusCode, convertResponse?.data)
                console.groupEnd()
            }

        } catch (err) {
            setAlert("서버 통신 중 문제가 발생하였습니다.")
            console.group(err.message)
            console.log(err.stack)
            console.groupEnd()
        }
    }

    return [data, request]
}

export default useFetch